/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Accordion, Stack, Text, useStyleConfig} from '@chakra-ui/react'
import AccordionItemBlock from './partials/accordion-item-block'

/**
 * @param {String} variant - string to set the style
 * @param {Boolean} isEditorial - boolean to check if its an editorial component
 * @param {String} title - string title of the component
 * @param {(Array|Object)} items - it can be an array or an object
 *
 * */

export const AccordionBlock = ({
    variant = 'Light Grey',
    accordionVariant = 'Light Grey',
    isEditorial = false,
    title,
    items,
    noPadding = false,
}) => {
    const styles = useStyleConfig('Accordion')

    if (!items || items?.length === 0) return null

    return (
        <Stack
            {...styles.accordionStack}
            {...(noPadding ? {} : styles.accordionStackPaddingBot)}
            backgroundColor={variant === 'White' ? 'none' : 'background.light'}
        >
            {title && (
                <Text {...styles.title} data-testid="accordion-title">
                    {title}
                </Text>
            )}
            <Stack {...styles.accordionContainer}>
                <Accordion {...styles.mainAccordion} allowMultiple>
                    {isEditorial ? (
                        items.map((item, index) => {
                            const isAccordionList = !!item?.values
                            const hasSecondaryTitle = !!item?.secondary_title
                            const hasBigFontSize = item?.accordion_title_font_size === 'Big'

                            return (
                                <AccordionItemBlock
                                    key={index}
                                    item={item}
                                    smallGap={noPadding}
                                    isAccordionList={isAccordionList}
                                    hasBigFontSize={hasBigFontSize}
                                    hasSecondaryTitle={hasSecondaryTitle}
                                    contrasted={
                                        variant === 'White' && accordionVariant === 'Light Grey'
                                    }
                                />
                            )
                        })
                    ) : (
                        <>
                            {/* Dynamically Render PDP Accordion Items */}

                            {items.map((item, index) => {
                                return (
                                    <AccordionItemBlock
                                        key={index}
                                        item={item}
                                        isAccordionList={true}
                                        hasBigFontSize={true}
                                        hasSecondaryTitle={false}
                                    />
                                )
                            })}
                        </>
                    )}
                </Accordion>
            </Stack>
        </Stack>
    )
}

AccordionBlock.propTypes = {
    variant: PropTypes.string,
    accordionVariant: PropTypes.string,
    isEditorial: PropTypes.bool,
    title: PropTypes.string,
    noPadding: PropTypes.bool,
    items: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
}
